import { BadgeSecondarySubtle, Icon, Tabs } from "@studio3t/generic-components";
import styled from "styled-components";
import { tabsWithProblems } from "./deployProblems";
import { usePipelineActionState } from "./pipelineActionsState";
import {
  countNonEmptyScripts,
  countSavedTests,
  type StageTabId,
  usePipelineStaging,
} from "./pipelineStaging";

export type { StageTabId };

export const STAGE_TABS_ID_BASE = "studio-stages";

// Figma Bridge_SecondaryTabs (14425-283750): the stage tab bar under the
// toolbar — Source / Transform / Sink / Tests, each with the section icon
// in a chip and a staged-count badge. The SCHEMA section has no tab in the
// design, so it isn't reachable from this bar for now. While the header
// shows Unresolved, the tabs whose editors hold a problem carry the dot
// (Figma Bridge_SecondaryTabs 14785-265833).
export function StageTabs({
  active,
  onChange,
}: {
  active: StageTabId;
  onChange: (id: StageTabId) => void;
}) {
  const { sources, transforms, sinks, tests, scriptDrafts } =
    usePipelineStaging();
  const alerts = tabsWithProblems(usePipelineActionState().problems);
  const tabs: ReadonlyArray<{
    id: StageTabId;
    label: string;
    icon: Icon;
    count: number;
  }> = [
    { id: "source", label: "Source", icon: Icon.Input, count: sources.length },
    {
      id: "transform",
      label: "Transform",
      icon: Icon.Transform,
      // An EMPTY untitled script is not a script yet (same spirit as the
      // Tests count below).
      count: countNonEmptyScripts(transforms, scriptDrafts),
    },
    { id: "sink", label: "Sink", icon: Icon.Output, count: sinks.length },
    {
      id: "tests",
      label: "Tests",
      // Only FINAL tests count — saved with content and a proper name;
      // untitled drafts aren't tests yet (same rule as the suite badge).
      icon: Icon.Tests,
      count: countSavedTests(tests),
    },
  ];

  return (
    <BarBox data-testid="studio-stage-tabs">
      <Tabs
        idBase={STAGE_TABS_ID_BASE}
        label="Pipeline stages"
        tabs={tabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
          badge: <BadgeSecondarySubtle>{tab.count}</BadgeSecondarySubtle>,
          alert: alerts.has(tab.id),
        }))}
        activeId={active}
        onChange={(id) => onChange(id as StageTabId)}
      />
    </BarBox>
  );
}

const BarBox = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.semanticAppBackground};
`;
